import { useState, useEffect } from "react";

const ROW_LENGTH_MIN = 5;
const ROW_LENGTH_MAX = 20;

const ClueBlockInput = ({ value, index, onChange, onRemove }) => {
    const [text, setText] = useState(String(value));

    useEffect(() => setText(String(value)), [value]);

    const handleBlur = () => {
        const parsed = parseInt(text, 10);
        if (Number.isInteger(parsed) && parsed > 0) {
            onChange(index, parsed);
        } else {
            setText(String(value));
        }
    };

    return (
        <div className="flex items-center gap-1">
            <input
                type="number"
                min={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={handleBlur}
                className="w-14 text-sm border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-badger-purple"
                aria-label={`Clue block ${index + 1}`}
            />
            <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                title="Remove block"
                aria-label={`Remove clue block ${index + 1}`}
            >
                ×
            </button>
        </div>
    );
};

export const ClueEditor = ({ rowLength, onRowLengthChange, clues, onCluesChange, error }) => {
    const [rowLengthText, setRowLengthText] = useState(String(rowLength));

    useEffect(() => setRowLengthText(String(rowLength)), [rowLength]);

    const handleRowLengthBlur = () => {
        const parsed = parseInt(rowLengthText, 10);
        if (Number.isInteger(parsed) && parsed >= ROW_LENGTH_MIN && parsed <= ROW_LENGTH_MAX) {
            onRowLengthChange(parsed);
        } else {
            setRowLengthText(String(rowLength));
        }
    };

    const handleBlockChange = (index, value) => {
        const next = [...clues];
        next[index] = value;
        onCluesChange(next);
    };

    const handleRemoveBlock = (index) => {
        onCluesChange(clues.filter((_, i) => i !== index));
    };

    const handleAddBlock = () => {
        onCluesChange([...clues, 1]);
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                <div className="flex items-center gap-2">
                    <label htmlFor="row-length" className="text-sm font-medium text-gray-700">Row length</label>
                    <input
                        id="row-length"
                        type="number"
                        min={ROW_LENGTH_MIN}
                        max={ROW_LENGTH_MAX}
                        value={rowLengthText}
                        onChange={(e) => setRowLengthText(e.target.value)}
                        onBlur={handleRowLengthBlur}
                        className="w-16 text-sm border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-badger-purple"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Clue</span>
                    {clues.map((value, index) => (
                        <ClueBlockInput
                            key={index}
                            value={value}
                            index={index}
                            onChange={handleBlockChange}
                            onRemove={handleRemoveBlock}
                        />
                    ))}
                    <button
                        type="button"
                        onClick={handleAddBlock}
                        className="text-xs text-badger-purple hover:underline px-1 min-h-[44px] sm:min-h-0"
                    >
                        + Add block
                    </button>
                </div>
            </div>

            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
    );
};
